import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkSupersub from 'remark-supersub';
import './Article.css';
import { Article as ArticleType, getArticleBySlug } from '../../utils/contentful';

interface CodeProps {
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
}

const Article: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<ArticleType | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) {
        setError('No article slug provided');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const fetchedArticle = await getArticleBySlug(slug);
        
        if (!fetchedArticle) {
          throw new Error(`Article with slug "${slug}" not found`);
        }
        
        setArticle(fetchedArticle);
      } catch (err) {
        console.error('Error loading article:', err);
        setError(err instanceof Error ? err.message : 'Failed to load article');
      } finally {
        setLoading(false);
      }
    };
    
    fetchArticle();
  }, [slug]);

  const handleShare = async () => {
    if (!article) return;
    
    const shareData = {
      title: article.title,
      text: `Check out this article: ${article.title}`,
      url: `${window.location.origin}/#/article/${slug}`
    };
    
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(`${window.location.origin}/#/article/${slug}`);
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  if (loading) {
    return (
      <div className="article">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="article">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error || 'Failed to load article'}</p>
          <Link to="/" className="back-link">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="article">
      <Link to="/" className="back-link">← Back to Home</Link>
      
      <div className="article-header">
        <h1>{article.title}</h1>
        <div className="article-metadata">
          <span className="article-date">
            {new Date(article.publishDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </span>
          <span className="article-reading-time">{article.readingTime}</span>
          <button className="share-button" onClick={handleShare}>
            Share
          </button>
        </div>
      </div>
      
      <div className="article-content">
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkSupersub]}
          components={{
            code({ inline, className, children, ...props }: CodeProps) {
              const match = /language-(\w+)/.exec(className || '');
              if (inline) {
                return (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              }
              
              // Get the language from the className
              const language = match ? match[1] : 'text';
              
              return (
                <div className="code-block-container">
                  <div className="code-language-label">{language}</div>
                  <pre>
                    <code className={className} {...props}>
                      {String(children).replace(/\n$/, '')}
                    </code>
                  </pre>
                </div>
              );
            },
            img: ({ node, ...props }) => (
              <img 
                {...props} 
                alt={props.alt || ''}
                className="article-image"
              />
            ),
          }}
        >
          {article.body}
        </ReactMarkdown>
      </div>
      
      <div className="artistic-pattern"></div>
      <div className="mvb-text">MvB</div>
    </div>
  );
};

export default Article; 